import { combineReducers, configureStore } from "@reduxjs/toolkit";

import { createConnectionMiddleware } from "../app/connection/connectionMiddleware";
import appReducer from "../services/AppService";
import { billingAPI } from "../services/BillingService";
import { orgAPI } from "../services/OrgService";
import timerReducer from "../services/TimerService";
import worksReducer from "../services/WorkReducer";
import { activityLogSlice } from "../slices/ActivityLogSlice";
import { aiSlice } from "../slices/AiSlice";
import { automationSlice } from "../slices/AutomationSlice";
import { clientPortalSlice } from "../slices/ClientPortalSlice";
import { contactNotesSlice } from "../slices/ContactNotesSlice";
import { contactsSlice } from "../slices/ContactsSlice";
import { contactTagsSlice } from "../slices/ContactTagsSlice";
import { customFieldsSlice } from "../slices/CustomFieldsSlice";
import { customReportsSlice } from "../slices/CustomReportsSlice";
import { documentsSlice } from "../slices/DocumentsSlice";
import { emailSequencesSlice } from "../slices/EmailSequencesSlice";
import { emailsSlice } from "../slices/EmailsSlice";
import { emailTemplatesSlice } from "../slices/EmailTemplatesSlice";
import { filesSlice } from "../slices/FilesSlice";
import { linkedProfilesSlice } from "../slices/LinkedProfilesSlice";
import { notificationCenterSlice } from "../slices/NotificationCenterSlice";
import { organizationNotesSlice } from "../slices/OrganizationNotesSlice";
import { portalSlice } from "../slices/PortalSlice";
import { qboSlice } from "../slices/QboSlice";
import { roleSlice } from "../slices/RoleSlice";
import { signaturesSlice } from "../slices/SignaturesSlice";
import { statusesSlice } from "../slices/StatusesSlice";
import { subscriptionSlice } from "../slices/SubscriptionSlice";
import { supportSlice } from "../slices/SupportSlice";
import { templatesSlice } from "../slices/TemplatesSlice";
import { twoStepSlice } from "../slices/TwoStepSlice";
import { userSlice } from "../slices/UserSlice";
import { workNotesSlice } from "../slices/WorkNotesSlice";
import { workRemindersSlice } from "../slices/WorkRemindersSlice";
import { workSlice } from "../slices/WorkSlice";
import { workTasksSlice } from "../slices/WorkTasksSlice";
import { workTypeSlice } from "../slices/WorkTypeSlice";

/**
 * Every RTK Query API of the app: the connection banner's refetch and the
 * two-step screen's refetch (QA batch 18) re-run their failed queries.
 */
export const API_SLICES = [
  emailsSlice,
  automationSlice,
  emailTemplatesSlice,
  emailSequencesSlice,
  activityLogSlice,
  subscriptionSlice,
  supportSlice,
  customReportsSlice,
  notificationCenterSlice,
  aiSlice,
  twoStepSlice,
  customFieldsSlice,
  portalSlice,
  clientPortalSlice,
  signaturesSlice,
  contactsSlice,
  linkedProfilesSlice,
  contactNotesSlice,
  organizationNotesSlice,
  userSlice,
  billingAPI,
  workSlice,
  orgAPI,
  statusesSlice,
  workTypeSlice,
  templatesSlice,
  filesSlice,
  documentsSlice,
  workNotesSlice,
  workTasksSlice,
  workRemindersSlice,
  contactTagsSlice,
  qboSlice,
  roleSlice,
];

const rootReducer = combineReducers({
  [emailsSlice.reducerPath]: emailsSlice.reducer,
  [automationSlice.reducerPath]: automationSlice.reducer,
  [emailTemplatesSlice.reducerPath]: emailTemplatesSlice.reducer,
  [emailSequencesSlice.reducerPath]: emailSequencesSlice.reducer,
  [activityLogSlice.reducerPath]: activityLogSlice.reducer,
  [subscriptionSlice.reducerPath]: subscriptionSlice.reducer,
  [supportSlice.reducerPath]: supportSlice.reducer,
  [customReportsSlice.reducerPath]: customReportsSlice.reducer,
  [notificationCenterSlice.reducerPath]: notificationCenterSlice.reducer,
  [aiSlice.reducerPath]: aiSlice.reducer,
  [twoStepSlice.reducerPath]: twoStepSlice.reducer,
  [customFieldsSlice.reducerPath]: customFieldsSlice.reducer,
  [portalSlice.reducerPath]: portalSlice.reducer,
  [clientPortalSlice.reducerPath]: clientPortalSlice.reducer,
  [signaturesSlice.reducerPath]: signaturesSlice.reducer,
  [contactsSlice.reducerPath]: contactsSlice.reducer,
  [linkedProfilesSlice.reducerPath]: linkedProfilesSlice.reducer,
  [contactNotesSlice.reducerPath]: contactNotesSlice.reducer,
  [organizationNotesSlice.reducerPath]: organizationNotesSlice.reducer,
  [filesSlice.reducerPath]: filesSlice.reducer,
  [documentsSlice.reducerPath]: documentsSlice.reducer,
  [billingAPI.reducerPath]: billingAPI.reducer,
  [workSlice.reducerPath]: workSlice.reducer,
  [statusesSlice.reducerPath]: statusesSlice.reducer,
  [orgAPI.reducerPath]: orgAPI.reducer,
  [userSlice.reducerPath]: userSlice.reducer,
  [workTypeSlice.reducerPath]: workTypeSlice.reducer,
  [templatesSlice.reducerPath]: templatesSlice.reducer,
  [workNotesSlice.reducerPath]: workNotesSlice.reducer,
  [workTasksSlice.reducerPath]: workTasksSlice.reducer,
  [workRemindersSlice.reducerPath]: workRemindersSlice.reducer,
  [contactTagsSlice.reducerPath]: contactTagsSlice.reducer,
  [qboSlice.reducerPath]: qboSlice.reducer,
  [roleSlice.reducerPath]: roleSlice.reducer,
  timerReducer,
  worksReducer,
  appReducer,
});

export const setupStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        emailsSlice.middleware,
        automationSlice.middleware,
        emailTemplatesSlice.middleware,
        emailSequencesSlice.middleware,
        activityLogSlice.middleware,
        subscriptionSlice.middleware,
        supportSlice.middleware,
        customReportsSlice.middleware,
        notificationCenterSlice.middleware,
        aiSlice.middleware,
        twoStepSlice.middleware,
        customFieldsSlice.middleware,
        portalSlice.middleware,
        clientPortalSlice.middleware,
        signaturesSlice.middleware,
        contactsSlice.middleware,
        linkedProfilesSlice.middleware,
        contactNotesSlice.middleware,
        organizationNotesSlice.middleware,
        userSlice.middleware,
        billingAPI.middleware,
        workSlice.middleware,
        orgAPI.middleware,
        statusesSlice.middleware,
        workTypeSlice.middleware,
        templatesSlice.middleware,
        filesSlice.middleware,
        documentsSlice.middleware,
        workNotesSlice.middleware,
        workTasksSlice.middleware,
        workRemindersSlice.middleware,
        contactTagsSlice.middleware,
        qboSlice.middleware,
        roleSlice.middleware,
        // QA batch 16 (NAV-026): "Can't reach ProCharted" banner + refetch.
        createConnectionMiddleware(
          API_SLICES as unknown as Parameters<
            typeof createConnectionMiddleware
          >[0],
        ),
      ),
  });
};
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
